import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { parseRoles } from '../../utils/rbac';
import { formatDate } from '../../utils/format';
import SowSelect from '../shared/SowSelect';
import { scopeRisksToSow, matchesRaidSearch } from '../../utils/raidScope';

// ── RAID type definitions ─────────────────────────────────────────────────────
// `label` is the plural, for the tabs and headings; `singular` is what one item is
// called. The singular is spelt out rather than trimmed off the plural, which turned
// Dependencies into "Dependencie".
const RAID_TYPES = [
  { value: 'risk',        label: 'Risks',         singular: 'Risk',        letter: 'R', bg: 'bg-red-50',     text: 'text-red-700',    border: 'border-red-200',    desc: 'Potential problems that could impact delivery' },
  { value: 'assumption',  label: 'Assumptions',   singular: 'Assumption',  letter: 'A', bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-200',   desc: 'Things assumed to be true for planning purposes' },
  { value: 'issue',       label: 'Issues',        singular: 'Issue',       letter: 'I', bg: 'bg-amber-50',   text: 'text-amber-700',  border: 'border-amber-200',  desc: 'Problems that have already occurred and need resolution' },
  { value: 'dependency',  label: 'Dependencies',  singular: 'Dependency',  letter: 'D', bg: 'bg-purple-50',  text: 'text-purple-700', border: 'border-purple-200', desc: 'External items or parties that the project relies on' },
];

// ── Risk constants ────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'delivery',    label: 'Delivery',    desc: 'Schedule slippage, missed milestones, delivery failure' },
  { value: 'technical',   label: 'Technical',   desc: 'Architecture, integration, technical debt, complexity' },
  { value: 'resource',    label: 'Resource',    desc: 'Key person dependency, skills gap, availability, attrition' },
  { value: 'commercial',  label: 'Commercial',  desc: 'Budget overrun, scope creep, PO delay, contract issues' },
  { value: 'security',    label: 'Security',    desc: 'Cyber threats, data breach, access control, compliance' },
  { value: 'vendor',      label: 'Vendor',      desc: 'Third-party dependency, supply chain, SLA breach' },
  { value: 'regulatory',  label: 'Regulatory',  desc: 'GDPR, industry compliance, audit, licensing' },
  { value: 'operational', label: 'Operational', desc: 'BAU handover, runbooks, support readiness, change mgmt' },
];

const STATUSES = [
  { value: 'open',        label: 'Open',        cls: 'bg-red-100 text-red-700' },
  { value: 'mitigating',  label: 'Mitigating',  cls: 'bg-amber-100 text-amber-700' },
  { value: 'accepted',    label: 'Accepted',    cls: 'bg-blue-100 text-blue-700' },
  { value: 'transferred', label: 'Transferred', cls: 'bg-purple-100 text-purple-700' },
  { value: 'closed',      label: 'Closed',      cls: 'bg-gray-100 text-gray-500' },
];

const TAGS = [
  { value: 'fsc', label: 'FSC', desc: 'Facility Security Clearance', cls: 'bg-green-100 text-green-700 border-green-200' },
];

// Status metadata across ALL RAID types (StatusBadge looks up here so every
// type's statuses render with the right label/colour).
const STATUS_META = {
  open:        { label: 'Open',        cls: 'bg-red-100 text-red-700' },
  mitigating:  { label: 'Mitigating',  cls: 'bg-amber-100 text-amber-700' },
  accepted:    { label: 'Accepted',    cls: 'bg-blue-100 text-blue-700' },
  transferred: { label: 'Transferred', cls: 'bg-purple-100 text-purple-700' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
  resolved:    { label: 'Resolved',    cls: 'bg-emerald-100 text-emerald-700' },
  validated:   { label: 'Validated',   cls: 'bg-emerald-100 text-emerald-700' },
  invalidated: { label: 'Invalidated', cls: 'bg-gray-200 text-gray-600' },
  pending:     { label: 'Pending',     cls: 'bg-amber-100 text-amber-700' },
  secured:     { label: 'Secured',     cls: 'bg-emerald-100 text-emerald-700' },
  at_risk:     { label: 'At Risk',     cls: 'bg-red-100 text-red-700' },
  closed:      { label: 'Closed',      cls: 'bg-gray-100 text-gray-500' },
};
// Statuses offered per RAID type — risk-scoring vocabulary only applies to risks.
const STATUS_BY_TYPE = {
  risk:       ['open', 'mitigating', 'accepted', 'transferred', 'closed'],
  assumption: ['open', 'validated', 'invalidated', 'closed'],
  issue:      ['open', 'in_progress', 'resolved', 'closed'],
  dependency: ['open', 'pending', 'secured', 'at_risk', 'closed'],
};
// A risk's 'open' reads as "New": one nobody has reviewed or mitigated yet, which
// is what the register is scanned for. The stored value is still 'open', so this
// is a label and nothing has to be migrated. Assumptions, issues and dependencies
// keep "Open", where it only ever meant the item is still live.
const RISK_STATUS_LABELS = { open: 'New' };
// Both words, for the All tab's filter: it sits above a mixed list where a risk's
// badge says New and an assumption's says Open.
const MIXED_STATUS_LABELS = { open: 'New / Open' };
// raidType is the type in view: one of the four, or 'all' on the first tab. Rows
// saved before the other three types existed carry no type and are risks, which
// is how the rest of the page reads them.
const statusLabel = (status, raidType) =>
  (raidType === 'all' ? MIXED_STATUS_LABELS[status] : undefined)
  || ((raidType || 'risk') === 'risk' ? RISK_STATUS_LABELS[status] : undefined)
  || STATUS_META[status]?.label || status;

// Per-type labels for the shared date + free-text fields.
const DATE_LABEL = { risk: 'Review Date', assumption: 'Review Date', issue: 'Target Resolution', dependency: 'Needed By' };
const NOTE_LABEL = { assumption: 'Rationale / basis', issue: 'Resolution / action', dependency: 'Detail / owning party' };
const NOTE_PLACEHOLDER = { assumption: 'Why this is assumed true, and how it will be validated…', issue: 'How this is being resolved and by whom…', dependency: 'What is depended on and who owns it (e.g. RR, supplier, 3rd party)…' };

const PROB_LABELS = { 1: 'Rare', 2: 'Unlikely', 3: 'Possible', 4: 'Likely', 5: 'Almost Certain' };
const IMPACT_LABELS = { 1: 'Negligible', 2: 'Minor', 3: 'Moderate', 4: 'Significant', 5: 'Critical' };

function scoreColor(score) {
  if (score >= 15) return 'bg-red-500 text-white';
  if (score >= 8)  return 'bg-amber-400 text-amber-950';
  if (score >= 4)  return 'bg-yellow-300 text-gray-800';
  return 'bg-emerald-400 text-emerald-950';
}
function scoreBorder(score) {
  if (score >= 15) return 'border-red-300';
  if (score >= 8)  return 'border-amber-300';
  if (score >= 4)  return 'border-yellow-200';
  return 'border-emerald-200';
}
function scoreLabel(score) {
  if (score >= 15) return 'HIGH';
  if (score >= 8)  return 'MEDIUM';
  if (score >= 4)  return 'LOW';
  return 'MINIMAL';
}

function RiskBadge({ score }) {
  return (
    <span className={`text-[7px] font-extrabold px-1.5 py-0.5 rounded ${scoreColor(score)}`}>
      {score} · {scoreLabel(score)}
    </span>
  );
}

function StatusBadge({ status, raidType }) {
  const s = STATUS_META[status] || STATUS_META.open;
  return <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${s.cls}`}>{statusLabel(status, raidType)}</span>;
}

// ── Risk matrix (5×5 grid) ────────────────────────────────────────────────────
export function RiskMatrix({ risks }) {
  const cellRisks = {};
  (risks || []).filter(r => r.status !== 'closed' && r.status !== 'transferred').forEach(r => {
    const key = `${r.probability}-${r.impact}`;
    if (!cellRisks[key]) cellRisks[key] = [];
    cellRisks[key].push(r);
  });
  const cellColor = (p, i) => {
    const s = p * i;
    if (s >= 15) return 'bg-red-100 border-red-300';
    if (s >= 8)  return 'bg-amber-50 border-amber-300';
    if (s >= 4)  return 'bg-yellow-50 border-yellow-200';
    return 'bg-emerald-50 border-emerald-200';
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="text-[9px] font-bold text-gray-600 mb-2 uppercase tracking-wider">Risk Matrix</div>
      <div className="flex gap-2">
        {/* Y-axis label */}
        <div className="flex flex-col justify-center items-center w-6">
          <span className="text-[7px] text-gray-400 font-semibold transform -rotate-90 whitespace-nowrap">Probability →</span>
        </div>
        <div className="flex-1">
          {/* Grid — probability rows (5 = top, 1 = bottom) */}
          {[5,4,3,2,1].map(p => (
            <div key={p} className="flex gap-1 mb-1">
              <div className="w-16 flex items-center">
                <span className="text-[7px] text-gray-500 font-semibold truncate">{PROB_LABELS[p]}</span>
              </div>
              {[1,2,3,4,5].map(i => {
                const key = `${p}-${i}`;
                const count = (cellRisks[key] || []).length;
                return (
                  <div key={i} className={`flex-1 min-h-[32px] border rounded flex items-center justify-center ${cellColor(p, i)}`}>
                    {count > 0 && (
                      <span className="text-[9px] font-extrabold text-gray-700" title={(cellRisks[key]||[]).map(r=>r.title).join('\n')}>{count}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {/* X-axis labels */}
          <div className="flex gap-1 mt-1">
            <div className="w-16" />
            {[1,2,3,4,5].map(i => (
              <div key={i} className="flex-1 text-center">
                <span className="text-[7px] text-gray-400 font-semibold">{IMPACT_LABELS[i].slice(0,3)}</span>
              </div>
            ))}
          </div>
          <div className="text-center text-[7px] text-gray-400 mt-0.5 font-semibold">Impact →</div>
        </div>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 mt-2">
        {[['bg-red-100 border border-red-300','HIGH (≥15)'],['bg-amber-50 border border-amber-300','MEDIUM (8-14)'],['bg-yellow-50 border border-yellow-200','LOW (4-7)'],['bg-emerald-50 border border-emerald-200','MINIMAL (<4)']].map(([cls, lbl]) => (
          <div key={lbl} className="flex items-center gap-1"><div className={`w-3 h-3 rounded ${cls}`} /><span className="text-[7px] text-gray-500">{lbl}</span></div>
        ))}
      </div>
    </div>
  );
}

// ── Risk form ─────────────────────────────────────────────────────────────────
const EMPTY_RISK = { title: '', description: '', categories: ['delivery'], probability: 3, impact: 3,
  residual_probability: null, residual_impact: null,
  status: 'open', owner_id: '', mitigation_plan: '', contingency_plan: '', review_date: '', sow_id: '', raid_type: 'risk', milestone_ids: [], tags: [],
  reason_category: '', owning_party: '', escalated: false };

function parseCategories(r) {
  if (!r) return ['delivery'];
  if (r.categories) {
    try { const p = JSON.parse(r.categories); return Array.isArray(p) ? p : [r.categories]; } catch { return [r.categories]; }
  }
  return r.category ? [r.category] : ['delivery'];
}
function parseMilestoneIds(r) {
  if (!r?.milestone_ids) return [];
  try { const p = JSON.parse(r.milestone_ids); return Array.isArray(p) ? p : []; } catch { return []; }
}
function parseTags(r) {
  if (!r?.tags) return [];
  try { const p = JSON.parse(r.tags); return Array.isArray(p) ? p : []; } catch { return []; }
}

function RiskFormModal({ initial, sows, people, reasonOpts = [], partyOpts = [], onClose, onSave }) {
  const [form, setForm] = useState(() => initial
    ? { ...initial, categories: parseCategories(initial), milestone_ids: parseMilestoneIds(initial), tags: parseTags(initial) }
    : { ...EMPTY_RISK });
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!form.sow_id) { setMilestones([]); return; }
    api.get(`sows/${form.sow_id}/milestones`).then(m => setMilestones(Array.isArray(m) ? m : [])).catch(() => setMilestones([]));
  }, [form.sow_id]);

  const toggleCategory = (val) => setForm(p => {
    const cats = p.categories || [];
    return { ...p, categories: cats.includes(val) ? cats.filter(c => c !== val) : [...cats, val] };
  });
  const toggleMilestone = (id) => setForm(p => {
    const ids = p.milestone_ids || [];
    return { ...p, milestone_ids: ids.includes(id) ? ids.filter(m => m !== id) : [...ids, id] };
  });
  const toggleTag = (val) => setForm(p => {
    const t = p.tags || [];
    return { ...p, tags: t.includes(val) ? t.filter(x => x !== val) : [...t, val] };
  });
  const score = (form.probability || 3) * (form.impact || 3);
  const residualScore = (form.residual_probability != null && form.residual_impact != null)
    ? parseInt(form.residual_probability) * parseInt(form.residual_impact)
    : null;
  const rt = RAID_TYPES.find(r => r.value === form.raid_type) || RAID_TYPES[0];
  const isRisk = form.raid_type === 'risk';
  // Switching type also snaps the status to one valid for that type ('open' is
  // shared by all, so it's a safe fallback).
  const setType = (v) => setForm(p => ({ ...p, raid_type: v, status: (STATUS_BY_TYPE[v] || ['open']).includes(p.status) ? p.status : 'open' }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    // Required structured fields (per house rules for project-health RAID).
    if ((form.raid_type === 'risk' || form.raid_type === 'issue') && !form.reason_category) {
      setError('Please choose a reason.'); return;
    }
    if (form.raid_type === 'dependency') {
      if (!form.owning_party) { setError('Please choose the owning party.'); return; }
      if (form.owning_party === '3rd Party' && !(form.mitigation_plan || '').trim()) {
        setError('Name the third party in the detail box.'); return;
      }
    }
    setSaving(true); setError('');
    try {
      await onSave({
        ...form,
        probability: parseInt(form.probability),
        impact: parseInt(form.impact),
        residual_probability: form.residual_probability != null ? parseInt(form.residual_probability) : null,
        residual_impact: form.residual_impact != null ? parseInt(form.residual_impact) : null,
        sow_id: form.sow_id || null,
        owner_id: form.owner_id || null,
        raid_type: form.raid_type || 'risk',
        categories: form.categories || ['delivery'],
        milestone_ids: form.milestone_ids || [],
        tags: form.tags || [],
        escalated: !!form.escalated,
        // Only meaningful for their respective types; cleared otherwise.
        reason_category: (form.raid_type === 'risk' || form.raid_type === 'issue') ? (form.reason_category || null) : null,
        owning_party: form.raid_type === 'dependency' ? (form.owning_party || null) : null,
      });
    } catch (e) { setError(e?.data?.error || e?.message || 'Save failed'); setSaving(false); }
  };

  return (
    <Modal
      title={initial ? `Edit ${initial.reference || 'item'}` : `New ${rt.singular}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-[9px] border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
            {saving ? 'Saving…' : `Save ${rt.singular}`}
          </button>
        </>
      }
    >
      <div className="space-y-3">

        {/* RAID type selector (risk score badge shown for risks only) */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1 flex-1">
            {RAID_TYPES.map(r => (
              <button key={r.value} type="button" onClick={() => setType(r.value)}
                className={`flex-1 py-1 rounded border-2 text-[9px] font-bold transition-all ${form.raid_type === r.value ? `${r.border} ${r.bg} ${r.text}` : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'}`}>
                {r.letter} <span className="hidden sm:inline">{r.label}</span>
              </button>
            ))}
          </div>
          {isRisk && (
            <span className={`text-[8px] font-extrabold px-2 py-1 rounded whitespace-nowrap ${scoreColor(score)}`}>
              {score} · {scoreLabel(score)}
            </span>
          )}
        </div>
        <div className="text-[8px] text-gray-400 -mt-1">{rt.desc}</div>

        {/* Title */}
        <div>
          <label className="form-label">Title *</label>
          <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)}
            placeholder={`Short ${rt.singular.toLowerCase()} description`} />
        </div>

        {/* Description */}
        <div>
          <label className="form-label">Description</label>
          <textarea className="form-textarea" rows="2" value={form.description}
            onChange={e => set('description', e.target.value)} placeholder="Detail and context…" />
        </div>

        {/* Reason — risks & issues (same taxonomy as missed-milestone reasons) */}
        {(form.raid_type === 'risk' || form.raid_type === 'issue') && (
          <div>
            <label className="form-label">Reason *</label>
            <select className="form-select" value={form.reason_category || ''} onChange={e => set('reason_category', e.target.value)}>
              <option value="">— select a reason —</option>
              {reasonOpts.map(r => <option key={r.id} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        )}

        {/* Categories — risk only */}
        {isRisk && (
          <div>
            <label className="form-label">Categories</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {CATEGORIES.map(c => {
                const checked = (form.categories || []).includes(c.value);
                return (
                  <button key={c.value} type="button" onClick={() => toggleCategory(c.value)}
                    className={`px-2 py-0.5 rounded border text-[8px] font-semibold transition-all ${checked ? 'border-navy bg-navy text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Compliance Tags */}
        <div>
          <label className="form-label">Compliance Tags</label>
          <div className="flex flex-wrap gap-1 mt-1">
            {TAGS.map(t => {
              const active = (form.tags || []).includes(t.value);
              return (
                <button key={t.value} type="button" onClick={() => toggleTag(t.value)}
                  title={t.desc}
                  className={`px-2 py-0.5 rounded border text-[8px] font-bold transition-all ${active ? `border-green-400 bg-green-100 text-green-700` : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'}`}>
                  {t.label}
                </button>
              );
            })}
            <span className="text-[8px] text-gray-400 self-center ml-1">FSC = Facility Security Clearance</span>
          </div>
        </div>

        {/* Company-wide / escalated: surfaces under the register's company-wide filter whatever project it belongs to */}
        <label className="flex items-center gap-2 text-[9px] text-gray-700 cursor-pointer">
          <input type="checkbox" className="accent-navy" checked={!!form.escalated} onChange={e => set('escalated', e.target.checked)} />
          <span className="font-semibold">Company-wide / Escalated Risk</span>
          <span className="text-gray-400">Shown under the register's company-wide / escalated filter as well as against its project</span>
        </label>

        {/* Status | Linked SOW | Milestone (milestone is risk-only; A/I/D attach to the SOW as a whole) */}
        <div className={`grid gap-2 ${isRisk ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
              {(STATUS_BY_TYPE[form.raid_type] || STATUS_BY_TYPE.risk).map(s => <option key={s} value={s}>{statusLabel(s, form.raid_type)}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Linked SOW <span className="text-gray-400 font-normal normal-case">{isRisk ? '' : '(project-level)'}</span></label>
            <SowSelect sows={sows} value={form.sow_id || ''} onChange={id => { set('sow_id', id); set('milestone_ids', []); }} placeholder="None" />
          </div>
          {isRisk && (
            <div>
              <label className="form-label">Milestone</label>
              {form.sow_id && milestones.length > 0 ? (
                <div className="space-y-0.5 max-h-24 overflow-y-auto border border-gray-200 rounded p-1 bg-white">
                  {milestones.map(m => {
                    const checked = (form.milestone_ids || []).includes(String(m.id));
                    return (
                      <label key={m.id} className={`flex items-center gap-1 px-1 py-0.5 rounded cursor-pointer text-[8px] ${checked ? 'bg-navy/5 text-navy font-semibold' : 'text-gray-500 hover:bg-gray-50'}`}>
                        <input type="checkbox" className="accent-navy flex-shrink-0" checked={checked} onChange={() => toggleMilestone(String(m.id))} />
                        <span className="truncate">{m.milestone_code}{m.description ? ` — ${m.description}` : ''}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="form-input text-[8px] text-gray-400 bg-gray-50 cursor-default">
                  {form.sow_id ? 'No milestones' : 'Select SOW first'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Probability + Impact sliders — risk only */}
        {isRisk && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Probability — <span className="text-gold font-bold">{PROB_LABELS[form.probability]}</span></label>
              <input type="range" min="1" max="5" value={form.probability} onChange={e => set('probability', e.target.value)} className="w-full accent-gold" />
              <div className="flex justify-between text-[7px] text-gray-400 mt-0.5"><span>Rare</span><span>Almost Certain</span></div>
            </div>
            <div>
              <label className="form-label">Impact — <span className="text-gold font-bold">{IMPACT_LABELS[form.impact]}</span></label>
              <input type="range" min="1" max="5" value={form.impact} onChange={e => set('impact', e.target.value)} className="w-full accent-gold" />
              <div className="flex justify-between text-[7px] text-gray-400 mt-0.5"><span>Negligible</span><span>Critical</span></div>
            </div>
          </div>
        )}

        {/* Owner + date (date label depends on type) */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="form-label">Owner</label>
            <select className="form-select" value={form.owner_id || ''} onChange={e => set('owner_id', e.target.value)}>
              <option value="">Unassigned</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">{DATE_LABEL[form.raid_type] || 'Review Date'}</label>
            <input type="date" className="form-input" value={form.review_date || ''} onChange={e => set('review_date', e.target.value)} />
          </div>
        </div>

        {isRisk ? (
          <>
            {/* Mitigation plan */}
            <div>
              <label className="form-label">Mitigation Plan</label>
              <textarea className="form-textarea" rows="2" value={form.mitigation_plan}
                onChange={e => set('mitigation_plan', e.target.value)} placeholder="Steps to reduce probability or impact…" />
            </div>
            {/* Contingency plan */}
            <div>
              <label className="form-label">Contingency Plan <span className="text-gray-400 font-normal">(if it materialises)</span></label>
              <textarea className="form-textarea" rows="2" value={form.contingency_plan}
                onChange={e => set('contingency_plan', e.target.value)} placeholder="What to do if this risk becomes an issue…" />
            </div>
          </>
        ) : (
          /* Assumption / issue / dependency: a free-text note, plus (for a
             dependency) a required owning-party dropdown. */
          <>
            {form.raid_type === 'dependency' && (
              <div>
                <label className="form-label">Owning party *</label>
                <select className="form-select" value={form.owning_party || ''} onChange={e => set('owning_party', e.target.value)}>
                  <option value="">— select owning party —</option>
                  {partyOpts.map(p => <option key={p.id} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="form-label">
                {form.raid_type === 'dependency' ? 'Detail' : (NOTE_LABEL[form.raid_type] || 'Notes')}
                {form.raid_type === 'dependency' && form.owning_party === '3rd Party' && <span className="text-red-500"> * name the third party</span>}
              </label>
              <textarea className="form-textarea" rows="2" value={form.mitigation_plan}
                onChange={e => set('mitigation_plan', e.target.value)}
                placeholder={form.raid_type === 'dependency'
                  ? (form.owning_party === '3rd Party' ? 'Name the third party and what it is waiting on…' : 'What is depended on and the detail…')
                  : (NOTE_PLACEHOLDER[form.raid_type] || '')} />
            </div>
          </>
        )}

        {/* Post-Mitigation Assessment — risk only */}
        {isRisk && (
        <div className="border border-blue-100 rounded-lg p-3 bg-blue-50/40">
          <div className="flex items-center justify-between mb-2">
            <label className="form-label mb-0">Post-Mitigation Assessment <span className="text-gray-400 font-normal">(residual risk after mitigations applied)</span></label>
            {residualScore != null && (
              <span className={`text-[8px] font-extrabold px-2 py-0.5 rounded ${scoreColor(residualScore)}`}>
                Residual: {residualScore} · {scoreLabel(residualScore)}
              </span>
            )}
          </div>
          {form.residual_probability == null ? (
            <button type="button" onClick={() => { set('residual_probability', form.probability); set('residual_impact', form.impact); }}
              className="text-[8px] text-blue-600 hover:text-blue-800 font-semibold underline">
              + Add residual score
            </button>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Residual Probability — <span className="text-blue-600 font-bold">{PROB_LABELS[form.residual_probability]}</span></label>
                  <input type="range" min="1" max="5" value={form.residual_probability}
                    onChange={e => set('residual_probability', parseInt(e.target.value))} className="w-full accent-blue-600" />
                  <div className="flex justify-between text-[7px] text-gray-400 mt-0.5"><span>Rare</span><span>Almost Certain</span></div>
                </div>
                <div>
                  <label className="form-label">Residual Impact — <span className="text-blue-600 font-bold">{IMPACT_LABELS[form.residual_impact]}</span></label>
                  <input type="range" min="1" max="5" value={form.residual_impact}
                    onChange={e => set('residual_impact', parseInt(e.target.value))} className="w-full accent-blue-600" />
                  <div className="flex justify-between text-[7px] text-gray-400 mt-0.5"><span>Negligible</span><span>Critical</span></div>
                </div>
              </div>
              <button type="button" onClick={() => { set('residual_probability', null); set('residual_impact', null); }}
                className="text-[8px] text-gray-400 hover:text-red-500 underline">
                Remove residual score
              </button>
            </div>
          )}
        </div>
        )}

        {form.status === 'closed' && (
          <div>
            <label className="form-label">Closure Notes</label>
            <textarea className="form-textarea" rows="2" value={form.closure_notes || ''}
              onChange={e => set('closure_notes', e.target.value)} placeholder="How was this resolved?" />
          </div>
        )}

        {error && <div className="text-[9px] text-red-600 font-semibold">{error}</div>}
      </div>
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RiskRegisterPage() {
  const { currentUser, detailId } = useApp();
  const [risks,   setRisks]   = useState([]);
  const [sows,    setSows]    = useState([]);
  const [people,  setPeople]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRisk, setEditRisk] = useState(null);   // null | 'new' | risk obj
  const [raidType,     setRaidType]     = useState('all');
  const [catFilter,    setCatFilter]    = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  // Deep-linked from a project (e.g. Project Update's "Open full RAID register")
  // pre-filters to that SOW and shows all owners for it.
  const [sowFilter,    setSowFilter]    = useState(detailId || 'all');
  const [tagFilter,    setTagFilter]    = useState('all');
  const [search,       setSearch]       = useState('');
  const [reasonOpts,   setReasonOpts]   = useState([]);   // missed_milestone_reason taxonomy (risks & issues)
  const [partyOpts,    setPartyOpts]    = useState([]);   // dependency_reason taxonomy (owning party)

  const userRoles   = parseRoles(currentUser?.user_role);
  const canEdit     = userRoles.some(r => ['admin','hr','pm','pmo'].includes(r));
  const isPrivileged = userRoles.some(r => ['admin','hr','pm','pmo'].includes(r)); // may edit any item
  // "Mine" = items I own or raised. Anyone without a privileged role may only
  // edit their own; admin/HR/PM/PMO may edit any item.
  const isMine = (r) => (r.owner_id && r.owner_id === currentUser?.id) || (r.raised_by && r.raised_by === currentUser?.id);
  const canEditItem = (r) => isPrivileged || isMine(r);
  // When deep-linked to a project, show every owner's items for it; otherwise a
  // PM defaults to their own.
  const [pmFilter, setPmFilter] = useState(() => (detailId || isPrivileged ? 'all' : 'mine'));
  // People who could own a RAID item (PMs / PMO), for the owner filter dropdown.
  const pmPeople = people.filter(p => parseRoles(p.user_role).some(r => ['pm','pmo'].includes(r)));

  const loadRisks = useCallback(() => {
    api.get('risks').then(setRisks).catch(() => setRisks([]));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('risks'),
      api.get('sows', { status: 'Active' }),
      api.get('people'),
    ]).then(([r, s, p]) => {
      setRisks(r || []); setSows(s || []); setPeople(p || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    // Structured taxonomies for the item form (shared with milestone reasons /
    // dependency parties so the vocabulary stays consistent across the app).
    api.get('config-options', { category: 'missed_milestone_reason' })
      .then(d => setReasonOpts(Array.isArray(d) ? d.filter(x => x.is_active !== 0) : [])).catch(() => {});
    api.get('config-options', { category: 'dependency_reason' })
      .then(d => setPartyOpts(Array.isArray(d) ? d.filter(x => x.is_active !== 0) : [])).catch(() => {});
  }, []);

  // Tell any other open tab (e.g. the Project Update page this was opened from)
  // that this project's RAID changed, so it can refresh where it linked from.
  const broadcastRaidChange = (sid) => {
    try { const ch = new BroadcastChannel('av:raid'); ch.postMessage({ sow_id: sid || null }); ch.close(); } catch { /* unsupported */ }
  };

  const handleSave = async (data) => {
    if (editRisk && editRisk !== 'new' && editRisk.id) {
      await api.put(`risks/${editRisk.id}`, data);
    } else {
      await api.post('risks', data);
    }
    setEditRisk(null);
    loadRisks();
    broadcastRaidChange(data.sow_id);
  };

  const handleClose = async (risk) => {
    if (!confirm(`Close risk "${risk.title}"? It will be marked as resolved.`)) return;
    await api.delete(`risks/${risk.id}`);
    loadRisks();
    broadcastRaidChange(risk.sow_id);
  };

  // Filtered view
  const filtered = risks.filter(r => {
    if (pmFilter === 'mine') { if (!isMine(r)) return false; }
    else if (pmFilter !== 'all') { if (r.owner_id !== pmFilter) return false; }
    if (raidType !== 'all' && (r.raid_type || 'risk') !== raidType) return false;
    if (catFilter !== 'all' && r.category !== catFilter) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (sowFilter !== 'all') {
      if (sowFilter === 'company' && r.sow_id && !r.escalated) return false;
      if (sowFilter !== 'company' && r.sow_id !== sowFilter) return false;
    }
    if (tagFilter !== 'all') {
      const t = parseTags(r);
      if (!t.includes(tagFilter)) return false;
    }
    if (!matchesRaidSearch(r, search)) return false;
    return true;
  });

  // Summary counts
  // Summary + matrix are risk-specific — scoring doesn't apply to A/I/D.
  // Both follow the SOW filter and the search box so the grid agrees with the list beneath it.
  const onlyRisks  = scopeRisksToSow(risks, sowFilter, search);
  const open       = onlyRisks.filter(r => r.status === 'open').length;
  const high       = onlyRisks.filter(r => r.risk_score >= 15 && r.status !== 'closed' && r.status !== 'transferred').length;
  const medium     = onlyRisks.filter(r => r.risk_score >= 8 && r.risk_score < 15 && r.status !== 'closed' && r.status !== 'transferred').length;
  const mitigating = onlyRisks.filter(r => r.status === 'mitigating').length;

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">RAID Register</h2>
          <div className="text-[9px] text-gray-400 mt-0.5">Risks · Assumptions · Issues · Dependencies — company-wide and project-level</div>
        </div>
        {canEdit && (
          <button onClick={() => setEditRisk('new')} className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ New Item</button>
        )}
      </div>

      {/* RAID type tabs */}
      <div className="flex items-center gap-1 mb-4 flex-wrap">
        <button
          onClick={() => setRaidType('all')}
          className={`px-3 py-1.5 text-[9px] font-semibold rounded-full border transition-all ${raidType === 'all' ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}>
          All ({risks.length})
        </button>
        {RAID_TYPES.map(rt => {
          const count = risks.filter(r => (r.raid_type || 'risk') === rt.value).length;
          return (
            <button key={rt.value} onClick={() => setRaidType(rt.value)}
              className={`px-3 py-1.5 text-[9px] font-semibold rounded-full border transition-all ${raidType === rt.value ? `${rt.bg} ${rt.text} ${rt.border}` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}>
              <span className="font-extrabold mr-1">{rt.letter}</span>{rt.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          ['New Risks', open, 'text-red-600'],
          ['High Risk', high, 'text-red-600'],
          ['Medium Risk', medium, 'text-amber-600'],
          ['Mitigating', mitigating, 'text-blue-600'],
        ].map(([lbl, val, cls]) => (
          <div key={lbl} className="bg-white border border-gray-200 rounded p-2">
            <div className="text-[7px] text-gray-400 uppercase font-bold">{lbl}</div>
            <div className={`text-xl font-extrabold ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      {/* Risk Matrix — only meaningful for risks, so hide on the A/I/D tabs */}
      {(raidType === 'all' || raidType === 'risk') && (
        <div className="mb-4">
          <RiskMatrix risks={onlyRisks} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search RAID items…" className="form-input text-[9px] py-1 max-w-[200px]" />

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="form-select text-[9px] py-1">
          <option value="all">All statuses</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{statusLabel(s.value, raidType)}</option>)}
        </select>

        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="form-select text-[9px] py-1">
          <option value="all">All categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>

        <div className="w-[220px]">
          <SowSelect
            sows={sows}
            value={sowFilter === 'all' || sowFilter === 'company' ? '' : sowFilter}
            onChange={id => setSowFilter(id || 'all')}
            placeholder="All projects — search by ref, title or client…"
            inputClassName="form-input text-[9px] py-1 w-full"
          />
        </div>

        <button
          onClick={() => setSowFilter(f => f === 'company' ? 'all' : 'company')}
          title="Items not linked to a specific project, plus any ticked as company-wide / escalated"
          className={`px-2.5 py-1 text-[9px] font-bold rounded border transition-all whitespace-nowrap ${sowFilter === 'company' ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-white text-gray-500 border-gray-200 hover:border-indigo-300'}`}>
          Company-wide / escalated risk
        </button>

        <select value={pmFilter} onChange={e => setPmFilter(e.target.value)} className="form-select text-[9px] py-1" title="Filter by owning PM">
          <option value="all">All owners</option>
          <option value="mine">My items</option>
          {pmPeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
        </select>

        <button
          onClick={() => setTagFilter(t => t === 'fsc' ? 'all' : 'fsc')}
          title="Facility Security Clearance"
          className={`px-2.5 py-1 text-[9px] font-bold rounded border transition-all ${tagFilter === 'fsc' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-gray-500 border-gray-200 hover:border-green-300'}`}>
          🔒 FSC only
        </button>

        <span className="text-[8px] text-gray-400 ml-auto">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Risk list */}
      {filtered.length === 0 && (
        <div className="text-center py-12 text-[10px] text-gray-400">
          <div className="text-3xl mb-2">🛡</div>
          <div className="font-semibold text-gray-500">No items match the current filters</div>
          {canEdit && <button onClick={() => setEditRisk('new')} className="mt-3 px-4 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ Add first item</button>}
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(r => {
          const rItemType = RAID_TYPES.find(x => x.value === (r.raid_type || 'risk')) || RAID_TYPES[0];
          const rIsRisk = (r.raid_type || 'risk') === 'risk';
          const cat = CATEGORIES.find(c => c.value === r.category);
          const reviewDue = r.review_date && new Date(r.review_date) < new Date();
          return (
            <div key={r.id} className={`bg-white border rounded-lg overflow-hidden ${rIsRisk ? scoreBorder(r.risk_score) : 'border-gray-200'}`}>
              <div className="flex items-start gap-3 px-4 py-3">
                {/* Leading tile — risk score for risks, a neutral type letter for A/I/D */}
                {rIsRisk ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center ${scoreColor(r.risk_score)}`}>
                      <span className="text-[12px] font-extrabold leading-none">{r.risk_score}</span>
                      <span className="text-[6px] font-bold uppercase leading-none mt-0.5">{scoreLabel(r.risk_score)}</span>
                    </div>
                    {r.residual_probability != null && r.residual_impact != null && (() => {
                      const rs = r.residual_probability * r.residual_impact;
                      return (
                        <>
                          <span className="text-[8px] text-gray-400 font-bold">→</span>
                          <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center border-2 border-dashed ${scoreColor(rs)}`} title="Residual score">
                            <span className="text-[12px] font-extrabold leading-none">{rs}</span>
                            <span className="text-[6px] font-bold uppercase leading-none mt-0.5">{scoreLabel(rs)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${rItemType.bg} ${rItemType.text} ${rItemType.border}`} title={rItemType.singular}>
                    <span className="text-[16px] font-extrabold leading-none">{rItemType.letter}</span>
                  </div>
                )}

                {/* Main content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-[8px] font-bold text-gray-400">{r.reference}</span>
                    <StatusBadge status={r.status} raidType={r.raid_type} />
                    <span className={`text-[7px] font-extrabold px-1.5 py-0.5 rounded ${rItemType.bg} ${rItemType.text}`}>{rItemType.letter} — {rItemType.singular}</span>
                    {rIsRisk && <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{cat?.label || r.category}</span>}
                    {r.sow_reference && (
                      <>
                        <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-navy/10 text-navy">{r.sow_reference}</span>
                        {(r.sow_detail || r.sow_client_name) && (
                          <span className="text-[7px] text-gray-400">
                            {r.sow_detail}{r.sow_detail && r.sow_client_name ? ' · ' : ''}{r.sow_client_name}
                          </span>
                        )}
                      </>
                    )}
                    {r.escalated ? (
                      <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200" title="Company-wide / escalated: shown under the company-wide filter as well as against its project">Escalated</span>
                    ) : null}
                    {parseTags(r).includes('fsc') && (
                      <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-200">🔒 FSC</span>
                    )}
                    {reviewDue && (
                      <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-600">⏰ Review overdue</span>
                    )}
                    {r.pre_staged ? (
                      <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600" title="Pre-staged — awaiting PM expansion of details">⚠ Pre-staged</span>
                    ) : null}
                  </div>
                  <div className="text-[10px] font-bold text-navy">{r.title}</div>
                  {r.description && <div className="text-[8px] text-gray-500 mt-0.5 line-clamp-2">{r.description}</div>}

                  <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                    {rIsRisk && (
                      <div className="text-[7px] text-gray-400">
                        Probability: <strong>{PROB_LABELS[r.probability]}</strong> · Impact: <strong>{IMPACT_LABELS[r.impact]}</strong>
                      </div>
                    )}
                    {r.owner_name && <div className="text-[7px] text-gray-400">Owner: <strong>{r.owner_name}</strong></div>}
                    {r.owning_party && <div className="text-[7px] text-gray-400">Owning party: <strong>{r.owning_party}</strong></div>}
                    {r.reason_category && <div className="text-[7px] text-gray-400">Reason: <strong>{r.reason_category}</strong></div>}
                    {r.review_date && <div className={`text-[7px] ${reviewDue ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{DATE_LABEL[r.raid_type] || 'Review'}: {formatDate(r.review_date)}</div>}
                  </div>

                  {r.mitigation_plan && (
                    <div className="mt-1.5 text-[8px] bg-blue-50 rounded p-1.5 text-blue-800">
                      <span className="font-bold">{rIsRisk ? 'Mitigation' : (NOTE_LABEL[r.raid_type] || 'Notes')}: </span>{r.mitigation_plan}
                    </div>
                  )}
                </div>

                {/* Actions — admin/HR/PM/PMO can edit any item; others only their own */}
                {canEditItem(r) && (
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => setEditRisk(r)} className="px-2 py-1 text-[8px] font-semibold border border-gray-200 rounded hover:bg-gray-50">Edit</button>
                    {r.status !== 'closed' && (
                      <button onClick={() => handleClose(r)} className="px-2 py-1 text-[8px] text-gray-400 hover:text-red-500 border border-transparent hover:border-red-200 rounded">Close</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit modal */}
      {editRisk && (
        <RiskFormModal
          initial={editRisk === 'new' ? null : editRisk}
          sows={sows}
          people={people}
          reasonOpts={reasonOpts}
          partyOpts={partyOpts}
          onClose={() => setEditRisk(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
